import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import {
  defaultStockOutward,
  OutwardSource,
  OutwardType,
  StockOutwardModal,
} from "../models/stock_transfer_model";
import { fetchGet } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import WarehouseSearchField from "../warehouse/warehouse_search_field";
import ItemRowOutward from "./ItemRowOutward";
import PartySearchField from "../../accounts/parties/party_search_field";
import { FileType } from "../../../utils/enums";
import ItemRowViewOutwardScreen from "./ItemRowViewOutwardScreen";
import { WarehouseModel } from "../models/warehouse_model";
import { getFileType } from "../../purchase_order/model/purchase_order_model";

interface StockJournalProps {}

const StockOutwardView: FunctionComponent<StockJournalProps> = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/stock_outward`;
  const [, setLoading] = useState(false);
  const [editable] = useState(false);
  const { showToast } = useToast();
  const [data, setData] = useState<StockOutwardModal>({
    ...defaultStockOutward,
  });
  const existingItems = data?.items || [];
  const [availableItems, setAvailableItems] = useState([]);
  const navigate = useHistory();

  useEffect(() => {
    if (id) getVoucher(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (data.warehouse) {
      getItemsInSelectedWarehouse(data.warehouse);
    }
  }, [data.warehouse]);

  const getItemsInSelectedWarehouse = async (warehouse: WarehouseModel) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses?warehouse_id=${warehouse.id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        const { item_data } = res.data;

        setAvailableItems(item_data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);

      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  return (
    <>
      <div className="sticky top-0 mt-2 px-6 h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Stock Outward Details
          </div>
        </div>
        <div className="bg-white rounded mb-5 flex gap-1 h-auto overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 p-5 text-sm w-3/4 font-semibold rounded-t top-0">
            <div className="">
              <div className="mr-0">
                <div className="font-bold">From Warehouse</div>
                <WarehouseSearchField
                  type={""}
                  disabled={true}
                  value={data.warehouse}
                  placeholder=""
                  onSelect={(d) => {
                    setData((old) => {
                      return {
                        ...old,
                        from_warehouse: d,
                      };
                    });
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold">
                  {" "}
                  Outward Type
                </label>
                <input
                  value={data.outward_type}
                  name=""
                  readOnly
                  id="tax_type"
                  className="p-1 rounded  focus:outline-none w-full border"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold"> Source</label>
                <input
                  value={data.source}
                  readOnly
                  name=""
                  id="tax_type"
                  className="p-1 rounded  focus:outline-none w-full border"
                />
              </div>
              <div className="">
                <label htmlFor="" className="text-sm font-bold">
                  Outward Date
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    data.outward_date
                      ? moment(data.outward_date).format("YYYY-MM-DD")
                      : ""
                  }
                  readOnly
                />
              </div>

              <div className="">
                <div className="font-bold">Responsible Person</div>
                <input
                  value={`${data.responsible_person || ""}`}
                  placeholder="responsible person"
                  type="text"
                  disabled={editable}
                  className="text-left p-1 border rounded focus:outline-none w-full"
                  readOnly
                />
              </div>
              <div>
                <div className="font-bold">Vendor</div>
                <PartySearchField
                  disabled={true}
                  value={data.vendor_name}
                  onSelect={(d) => {
                    setData((o) => ({
                      ...o,
                      vendor_name: d,
                    }));
                  }}
                />
              </div>
              <div className="my-2">
                <label>Remark</label>
                <input
                  disabled={true}
                  value={data?.remark}
                  name="remark"
                  id="remark"
                  placeholder="Remark"
                  type="text"
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  onChange={(e) =>
                    setData((d) => ({
                      ...d,
                      remark: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="">
              <div className="mr-0">
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="line1">
                    Responsible Person Address
                  </label>
                  <textarea
                    placeholder="address"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="address"
                    name="address"
                    rows={3}
                    value={data.responsible_person_address}
                    readOnly
                  />
                </div>
              </div>
              {data.outward_type !== OutwardType["Finished Good Sale"] && (
                <>
                  <div className="my-2">
                    <div className="flex flex-col">
                      <label className="font-semibold">PO No.</label>
                      <input
                        defaultValue={data.po_no}
                        placeholder="Enter PO Number"
                        disabled={true}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">PO Vendor</label>
                    <input
                      value={data.po_vendor_name || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>
                </>
              )}

              {data.outward_type === OutwardType["Finished Good Sale"] && (
                <>
                  <div className="my-2">
                    <div className="flex flex-col">
                      <label className="font-semibold">Order ID *</label>
                      <input
                        defaultValue={data.order_id}
                        placeholder="Enter Order ID"
                        disabled={true}
                      />
                    </div>
                  </div>

                  <div className="my-2">
                    <div className="flex flex-col">
                      <label className="font-semibold">Organization *</label>
                      <input
                        defaultValue={data.org_namespace}
                        placeholder="Enter Order ID"
                        disabled={true}
                      />
                    </div>
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Customer Name</label>
                    <input
                      value={data.customer_name || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Order Status</label>
                    <input
                      value={data.order_status || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Sales Type</label>
                    <input
                      value={data.sales_type || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>
                </>
              )}
              <div className="mt-4">
                {data.attachments &&
                  Array.isArray(data.attachments) &&
                  data.attachments.map((fileObj) => (
                    <div
                      key={fileObj.id}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <div className="flex items-center gap-3 m-4">
                        {getFileType(fileObj.type) === FileType.IMAGE ? (
                          <img
                            src={fileObj.url}
                            alt="Attachment"
                            className="w-16 h-16 object-cover"
                          />
                        ) : (
                          <a
                            href={fileObj.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm"
                          >
                            {fileObj.url.split("/").pop()}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 mt-5 ">
          <div className="flex flex-col gap-5">
            <div className="">
              <h3 className="text-sm font-bold">Particulars</h3>
              <div className="bg-white rounded flex flex-col gap-1 h-auto overflow-auto pb-20">
                {data.source === OutwardSource["In-transit"] && (
                  <div>
                    <div className="grid grid-cols-10 gap-1 p-1 bg-myPrimaryColor text-white text-sm font-semibold rounded-t sticky top-0">
                      <div className="text-left">InTransit ID</div>
                      <div className="text-left">Stock Transfer ID</div>
                      <div className="text-left">Item Code</div>
                      <div className="text-left col-span-2">Item Name</div>
                      <div className="text-left">Device IDs</div>
                      <div className="text-left">Qty</div>
                      <div className="text-left">Price</div>
                      <div className="text-left">Amount</div>
                      <div className="text-center">Actions</div>
                    </div>
                    <div>
                      {data.items.map((comp, i) => {
                        return (
                          <ItemRowViewOutwardScreen
                            component={comp}
                            from_warehouse={data.warehouse}
                            source={data.source}
                            edit={false}
                            disabled={true}
                            key={comp.id}
                            onDelete={(d: any) => {
                              setData((o) => {
                                const items = [
                                  ...(o.items || []).filter(
                                    (it) => it.id !== d.id
                                  ),
                                ];

                                return {
                                  ...o,
                                  items,
                                };
                              });
                            }}
                            onSubmit={(d: any) => {
                              setData((o) => {
                                const items = [
                                  ...(o.items || []).map((it) =>
                                    it.id === d.id ? d : it
                                  ),
                                ];

                                return {
                                  ...o,
                                  items,
                                };
                              });
                            }}
                            existingItems={existingItems}
                            availableItems={availableItems}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {data.source === OutwardSource["Stock inventory"] && (
                  <div>
                    <div className="grid grid-cols-9 gap-1 p-1 bg-myPrimaryColor text-white text-sm font-semibold rounded-t sticky top-0">
                      <div className="text-left">Item Code</div>
                      <div className="text-left col-span-2">Item Name</div>
                      <div className="text-left">Unit Measure</div>
                      <div className="text-left">Qty</div>
                      <div className="text-left">Device IDs</div>

                      <div className="text-left">Price</div>
                      <div className="text-left">Amount</div>
                    </div>

                    <div>
                      {data.items.map((comp, i) => {
                        return (
                          <ItemRowOutward
                            component={comp}
                            from_warehouse={data.warehouse}
                            source={data.source}
                            edit={false}
                            disabled={true}
                            key={comp.id}
                            onDelete={(d: any) => {
                              setData((o) => {
                                const items = [
                                  ...(o.items || []).filter(
                                    (it) => it.id !== d.id
                                  ),
                                ];

                                return {
                                  ...o,
                                  items,
                                };
                              });
                            }}
                            onSubmit={(d: any) => {
                              setData((o) => {
                                const items = [
                                  ...(o.items || []).map((it) =>
                                    it.id === d.id ? d : it
                                  ),
                                ];

                                return {
                                  ...o,
                                  items,
                                };
                              });
                            }}
                            existingItems={existingItems}
                            availableItems={availableItems}
                            outward_type={data.outward_type}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex sticky bottom-0 justify-end my-5 gap-4 px-5 py-5">
        <button
          type="button"
          className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
          onClick={() => navigate.push(urls.stock_outward)}
        >
          Quit
        </button>
      </div>
    </>
  );
};

export default StockOutwardView;
